import "./style.css";

import type { GameState, Viewport } from "./sim/types";
import { band } from "./sim/bands";
import { centreFor } from "./sim/constants";
import { sectorNumeral } from "./sim/scene";
import {
  clearBuffer,
  createState,
  nearestThreat,
  press,
  setReduceMotion,
  step,
  useFreeze,
} from "./sim/sim";
import { draw } from "./render/draw";
import { Audio } from "./audio/audio";
import { bindKeyboard, buildKeypad } from "./input/controls";
import {
  buildPayload,
  configured,
  fetchTop,
  flushPending,
  localBest,
  rememberName,
  savedName,
  submit,
  type Entry,
} from "./net/leaderboard";

/* ---------- canvas ---------- */
const canvas = document.getElementById("game") as HTMLCanvasElement;
const cx = canvas.getContext("2d")!;
const vp: Viewport = { w: 800, h: 520 };

function resize(): void {
  const rect = canvas.parentElement!.getBoundingClientRect();
  const dpr = Math.min(devicePixelRatio || 1, 2);
  vp.w = Math.max(320, Math.floor(rect.width));
  vp.h = Math.max(380, Math.floor(Math.min(620, innerHeight * 0.64)));
  canvas.width = vp.w * dpr;
  canvas.height = vp.h * dpr;
  canvas.style.height = `${vp.h}px`;
  cx.setTransform(dpr, 0, 0, dpr, 0, 0);
}
addEventListener("resize", resize);

setReduceMotion(matchMedia("(prefers-reduced-motion: reduce)").matches);

/* ---------- estado ---------- */
const audio = new Audio();
let S: GameState = createState(1);
let submitted = false;

const el = <T extends HTMLElement>(id: string): T => document.getElementById(id) as T;

const overlay = el("over");
const overP = el<HTMLParagraphElement>("over-p");
const submitBox = el("submit-box");
const submitMsg = el<HTMLParagraphElement>("submit-msg");
const nameInput = el<HTMLInputElement>("name");
const sendBtn = el<HTMLButtonElement>("send");
const board = el<HTMLOListElement>("board");

function newRun(startWave: number): void {
  const tempo = S.tempo;
  const depth = S.depth;
  S = createState(startWave);
  S.tempo = tempo;
  S.depth = depth;
  S.startedAt = performance.now();
  submitted = false;
  overlay.classList.remove("on");
  submitBox.classList.remove("done");
  submitMsg.textContent = "";
  submitMsg.className = "tiny";
  board.innerHTML = "";
}

/* ---------- fin de partida ---------- */
function renderBoard(rows: Entry[], myScore: number): void {
  board.innerHTML = rows
    .map((r) => {
      const me = r.score === myScore ? " class=\"me\"" : "";
      const name = r.name.replace(/[<>&]/g, "");
      return `<li${me}><span class="r">${r.rank}</span><span class="n">${name}</span><span class="s">${r.score.toLocaleString("es-MX")}</span></li>`;
    })
    .join("");
}

async function showGameOver(): Promise<void> {
  const best = localBest();
  overP.textContent =
    `Puntos ${S.score.toLocaleString("es-MX")} · ola ${S.wave} · ` +
    `banda mas dura ${S.hardestRun} (${band(S.hardestRun).label})` +
    (best > S.score ? ` · tu mejor: ${best.toLocaleString("es-MX")}` : "");

  nameInput.value = savedName();
  if (!configured()) {
    submitBox.classList.add("done");
    submitMsg.textContent = "Leaderboard sin configurar (revisa .env)";
    submitMsg.className = "tiny bad";
  }
  overlay.classList.add("on");

  const rows = await fetchTop(10);
  if (rows.length) renderBoard(rows, -1);
}

sendBtn.onclick = async () => {
  if (submitted) return;
  const name = nameInput.value.trim();
  if (!name) {
    submitMsg.textContent = "Escribe un nombre";
    submitMsg.className = "tiny bad";
    return;
  }
  sendBtn.disabled = true;
  submitMsg.textContent = "Enviando…";
  submitMsg.className = "tiny";
  rememberName(name);

  const res = await submit(buildPayload(S, name));
  sendBtn.disabled = false;
  if (res.ok) {
    submitted = true;
    submitBox.classList.add("done");
    submitMsg.textContent = `Posicion #${res.rank}`;
    submitMsg.className = "tiny ok";
    renderBoard(await fetchTop(10), S.score);
  } else {
    submitMsg.textContent = res.message;
    submitMsg.className = "tiny bad";
  }
};

/* ---------- entrada ---------- */
const handlers = {
  digit: (d: string) => {
    audio.init();
    press(S, d, vp);
  },
  clear: () => clearBuffer(S),
  freeze: () => {
    audio.init();
    useFreeze(S);
  },
};
bindKeyboard(handlers);
const freezeBtn = buildKeypad(el("pad"), handlers);

el<HTMLButtonElement>("restart").onclick = () => newRun(S.startWave);

const soundBtn = el<HTMLButtonElement>("sound");
soundBtn.onclick = async () => {
  const on = await audio.toggle();
  soundBtn.textContent = `Sonido: ${on ? "encendido" : "apagado"}`;
};

/* ---------- HUD ---------- */
let lastOver = false;
function syncHud(): void {
  el("h-wave").textContent = String(S.wave);
  el("h-score").textContent = S.score.toLocaleString("es-MX");

  const lives = el("h-lives");
  lives.textContent = S.lives > 0 ? "\u25A0 ".repeat(S.lives).trim() : "\u2014";
  lives.className = `v${S.lives <= 1 ? " warn" : ""}`;

  el("h-pw").textContent =
    S.freeze > 0
      ? `${(S.freeze / 1000).toFixed(1)}s`
      : "\u25C6 ".repeat(S.charges).trim() || "\u2014";

  el("h-sector").textContent = sectorNumeral(S.wave);
  el("h-band").textContent = band(S.hardestWave).label;
  freezeBtn.disabled = S.charges <= 0 || S.freeze > 0;

  if (S.over && !lastOver) void showGameOver();
  lastOver = S.over;
}

/* ---------- loop ---------- */
let last = 0;
function frame(now: number): void {
  const dt = Math.min(40, now - last) || 16;
  last = now;

  step(S, now, dt, vp);

  if (S.events.length) {
    audio.handle(S.events);
    S.events.length = 0;
  }
  if (!S.over && S.freeze <= 0 && S.inter <= 0) {
    audio.bed(now, S.wave, nearestThreat(S, vp));
  }

  draw(cx, S, now, vp);
  syncHud();
  requestAnimationFrame(frame);
}

/* ---------- arranque ---------- */
resize();
newRun(1);
void flushPending();
// también informa el centro de dificultad actual, útil al saltar de ola
console.info(
  `MODULO listo. Centro de banda en ola 1: ${centreFor(1).toFixed(2)}. ` +
    `Leaderboard ${configured() ? "configurado" : "sin configurar"}.`,
);
requestAnimationFrame(frame);
